import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../helpers/download';
import {getWhitelistedParentUrl} from '../../helpers/environment';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, Popover, pivotData, formatDate} from 'wc-lib';
import {format} from 'date-fns';
import { url } from 'inspector';
import { ElectricityRegions } from './config';


require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/arrowright.svg');

@Component({
  template: require('./electricity-dashboard.html'),
  name: 'electricity-dashboard',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class ElectricityDashboard extends Vue {
  data: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  regions = ElectricityRegions;
  disabledRegions = [];
  selectedRegion: string = 'NSW1';
  selectedPeriod: string = '30MIN';
  currentInterval: any = {};
  intervalRefresh: any;
  id: string;
  guideColour = '#777da7';
  chartConfig: any  = null;
  parentUrl = getWhitelistedParentUrl();
  nemDashboardUrl = this.parentUrl + '/Energy-systems/Electricity/National-Electricity-Market-NEM/Data-NEM/Data-Dashboard-NEM';
  waDashboardUrl = this.parentUrl + '/Energy-systems/Electricity/Wholesale-Electricity-Market-WEM/Data-WEM/Data-dashboard';

  get priceLabel() {
    if (this.selectedRegion === 'WA') {
      return 'MARKET CLEARING PRICE';
    } else {
      return 'CURRENT SPOT PRICE';
    }
  }

  get chartPriceLabel() {
    if (this.selectedRegion === 'WA') {
      return 'MARKET CLEARING PRICE';
    } else {
      return 'PRICE';
    }
  }

  get demandLabel() {
    if (this.selectedRegion === 'WA') {
      return 'GENERATION';
    } else {
      return 'CURRENT DEMAND';
    }
  }

  get chartDemandLabel() {
    if (this.selectedRegion === 'WA') {
      return 'GENERATION';
    } else {
      return 'DEMAND';
    }
  }

  get isWa() {
    return this.selectedRegion ==='WA';
  }

  get isRegionDisabled() {
    return this.disabledRegions.includes(this.selectedRegion);
  }

  created() {
    this.updateDisabledRegions();
    this.fetchData();
    this.intervalRefresh = setInterval(() => {
      this.fetchData();
    }, 10000);
  }

  updateDisabledRegions() {
    if (this.$route.query.Elec_location) {
      const regions = (this.$route.query.Elec_location as string).split(',');
      this.disabledRegions = regions;
    }
  }

  async fetchData() {
    const now = new Date();
    if (this.isWa) {
      this.id = await DataManager.addEndPoint(`../../data/WA/infographic/neartime/pulse.csv?datetime=${now.getTime()}`, undefined, 60000, undefined,'csv','yyyy-MM-ddTHH:mm:ss',['TRADING_DAY_INTERVAL','AS_AT']);
    } else {
      this.id = await DataManager.addEndPoint('../api/report/5MIN', {"timeScale":[this.selectedPeriod]}, 60000, '5MIN', undefined, 'yyyy-MM-ddTHH:mm:ss', ['SETTLEMENTDATE']);
    }
       
    this.dynamicView = DataManager.getDynamicView(this.id, 'elecsummary');
    this.applyFilter();
    this.chartConfig = this.getChartConfig(this.isWa);
  }

  applyFilter() {
    this.dynamicView.removeFilters();
    if (!this.isWa) this.dynamicView.applyFind({ 'REGIONID': this.selectedRegion });
    let data = this.dynamicView.data();

    let currentInterval = null;
    
    for (let i = data.length - 1; i >= 0; i--) {
      let row = data[i];

      if (this.isWa) {
        // Transform WA data to simplify graph rendering code
        if (row.PRICE || row.FORECAST_MW) {
          row.RRP = parseFloat(row.PRICE);
          row.SETTLEMENTDATE = row.TRADING_DAY_INTERVAL;
          if (row.ACTUAL_TOTAL_GENERATION) {
            row.PERIODTYPE = "ACTUAL";
            row.TOTALDEMAND = parseFloat(row.ACTUAL_TOTAL_GENERATION);
          } else if (row.FORECAST_EOI_MW) {
            row.PERIODTYPE = "FORECAST";
            row.TOTALDEMAND = parseFloat(row.FORECAST_EOI_MW);
          } else {
            row.PERIODTYPE = "FORECAST";
            row.TOTALDEMAND = parseFloat(row.FORECAST_MW);
          }
        }
      }

      if (row.PERIODTYPE === 'ACTUAL') {
        if (!currentInterval) {
          currentInterval = row;
          row.dashLength = 6;
        }
      }
    }

    this.currentInterval = currentInterval;
    this.data = data;
  }

  chartRendered(chart: any) {
    this.addChartLabels(chart);
    chart.addListener('drawn', (e: any) => {
      e.chart.clearLabels();
      this.addChartLabels(e.chart);
    });
  }

  addChartLabels(chart: any) {
    const yTitle = 26,
          yLabel = '!60',
          yValue = '!46',
          titleSize = 10,
          labelSize = 8,
          valueSize = 12,
          titleOpacity = 0.6,
          labelOpacity = 1,
          valueOpacity = 1;


    if ((this.isWa || this.selectedPeriod !== '5MIN') && chart.categoryAxis.dateToCoordinate && this.currentInterval.SETTLEMENTDATE) {
      let xCoordinate = chart.categoryAxis.dateToCoordinate(this.currentInterval.SETTLEMENTDATE);
      if (xCoordinate) xCoordinate += 30;
      if (isNaN(xCoordinate)) return;

      const value = formatDate(this.currentInterval.SETTLEMENTDATE, "HH:mm dd MMM");
      chart.addLabel(xCoordinate, yTitle, 'FORECAST', 'left', titleSize, this.guideColour, 0, titleOpacity, true);
      chart.addLabel(xCoordinate, yValue, value, 'left', valueSize, this.guideColour, 0, valueOpacity, true);

      if (this.isWa) {
        chart.addLabel(xCoordinate, yLabel, 'CURRENT TIME', 'left', labelSize, this.guideColour, 0, labelOpacity, true);
      } else if (!this.isWa && this.currentInterval.SETTLEMENTDATE) {
        chart.addLabel(xCoordinate, yLabel, 'CURRENT (SPOT) TIME', 'left', labelSize, this.guideColour, 0, labelOpacity, true);
      }
    }
  }

  @Watch('selectedPeriod')
  onSelectedPeriodChange(newValue: string, oldValue: string) {
    this.fetchData();
  }

  @Watch('selectedRegion')
  onSelectedRegionChange(newValue: string, oldValue: string) {

    this.fetchData();

  }

  getChartConfig(isWa: boolean) {
    const guides = [];
    if (this.isWa || this.selectedPeriod === '30MIN') {
      guides.push({
        date: this.currentInterval.SETTLEMENTDATE,
        color: this.guideColour,
        lineColor: "#777da7",
        lineAlpha: 1,
        fillAlpha: 0.1,
        dashLength: 3,
        lineThickness: 2,
        inside: false,
        above: true
      });
    }

    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 1,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0
      }, {
        id: "v2",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0
      }],
      graphs: [ 
        { 
          valueAxis: "v1", 
          balloonText: `${this.chartDemandLabel} [[value]] MW`, 
          valueField: 'TOTALDEMAND', 
          lineColor: '#34B9B3',
          precision: 0, 
          dashLengthField: "dashLength",
          bulletSize: 25, 
          bulletOffset: 10, 
          lineThickness: 5, 
          fillAlphas: 0.3,
          fillColors: ['#34B9B3', '#ffffff'],
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000'
          }
        },
        { 
          valueAxis: "v2", 
          balloonText: `${this.chartPriceLabel} $[[value]]/MWh`, 
          lineColor: '#B056BC',
          valueField: 'RRP', 
          bulletSize: 25, 
          lineThickness: 3, 
          dashLengthField: "dashLength",
          balloonColor:'#000000',
          balloon:{
            color:'#ffffff',
            fillColor:'#000000',
            showBullet:false
          }
        }
      ],
      
      categoryAxis: {
        parseDates: true,
        //labelRotation: 45,
        minPeriod: "mm",
        color: '#ffffff',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0
      },
      categoryField: "SETTLEMENTDATE",
      guides: guides,
      chartCursor: {
        zoomable: false,
        bulletsEnabled: true,
        bulletSize: 12,
        cursorColor: '#000000',
        categoryBalloonFunction: function(value) {
          var valueIndex = this.xToIndex(this.dateToCoordinate(value)),
              value = this.data[valueIndex],
              actual = value.dataContext.PERIODTYPE === 'ACTUAL',
              label = '';
          
          if (valueIndex == 0 ) {
            label = formatDate(value.dataContext.SETTLEMENTDATE, "HH:mm dd MMM");
          } else {
            label = formatDate(this.data[valueIndex-1].dataContext.SETTLEMENTDATE, "HH:mm-") + 
            formatDate(value.dataContext.SETTLEMENTDATE, "HH:mm dd MMM");
          } 
          
          if (isWa) {
            return label + ' Forecast';
          }
          label += actual ? ' Actual' : ' Forecast';
          
          return label;
        }
      }
    };
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }
}



// WEBPACK FOOTER //
// src/views/electricity/electricity-dashboard.ts